import { Injectable, ComponentRef, Injector, EnvironmentInjector, createComponent } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { TemplatePrintOptions, TemplateContext } from 'src/app/models/reportTemplate.model';
import { getWidgetsForEntityType, getWidgetByComponentName } from 'src/app/shared/widget-registry';
import { environment } from 'src/environments/environment';
import { getRegionApiUrl } from 'src/app/_helpers/region.helper';
import { buildPreviewResetCss } from './preview-css.constants';
import { escapeStyleClose } from 'src/app/shared/helpers/report-html-sanitizer.helper';
import { TemplateMapperRegistry } from 'src/app/services/template-mappers/template-mapper-registry';

export interface RenderPdfRequest {
    html: string;
    printOptions: RenderPdfPrintOptions;
}

export interface RenderPdfPrintOptions {
    pageSize: string;
    pageOrientation: string;
    margins: string;
    scale: number;
    headerHtml: string;
    footerHtml: string;
}

const DEFAULT_PRINT_OPTIONS = {
    pageSize: 'A4',
    pageOrientation: 'portrait',
    margins: '0mm',
    scale: 1,
    headerHtml: '',
    footerHtml: '',
    contentPadding: '0px'
} as const;

@Injectable()
export class PdfGeneratorService {
    private createdComponents: ComponentRef<unknown>[] = [];
    private activeTimeouts: number[] = [];
    private blobCache = new Map<string, { blob: Blob; htmlContent: string }>();
    private readonly MAX_CACHE_SIZE = 5;

    constructor(
        private injector: Injector,
        private environmentInjector: EnvironmentInjector,
        private http: HttpClient,
        private mapperRegistry: TemplateMapperRegistry
    ) { }

    async generatePdfBlob(
        htmlContent: string,
        printOptions: TemplatePrintOptions,
        entityType?: string
    ): Promise<{ blob: Blob; htmlContent: string }> {
        const mappedOptions = this.mapPrintOptions(printOptions);
        const cacheKey = this.computeCacheKey(htmlContent, mappedOptions, entityType);

        const cached = this.blobCache.get(cacheKey);
        if (cached) {
            this.blobCache.delete(cacheKey);
            this.blobCache.set(cacheKey, cached);
            return cached;
        }

        const styledHtml = this.buildStyledHtml(htmlContent, printOptions, entityType);
        const request: RenderPdfRequest = {
            html: styledHtml,
            printOptions: mappedOptions
        };

        const json = JSON.stringify(request);
        const { body, headers } = await this.encodeRequestBody(json);

        const blob = await firstValueFrom(
            this.http
                .post(`${getRegionApiUrl()}/Report/RenderPdf`, body, { headers, responseType: 'blob' })
                // DASH-2756: the client timeout must stay strictly greater than the server-side render
                // budget (PdfRenderSettings.MaxRequestTimeoutMs = 45s render + up to 20s queue wait) so
                // the server always fails first with an actionable 504 instead of the client giving up
                // on a still-running render. 90s leaves comfortable headroom above the server worst case.
                .pipe(timeout(90000))
        );

        const result = { blob, htmlContent: styledHtml };
        this.blobCache.set(cacheKey, result);
        while (this.blobCache.size > this.MAX_CACHE_SIZE) {
            const oldestKey = this.blobCache.keys().next().value;
            if (oldestKey !== undefined) this.blobCache.delete(oldestKey);
        }
        return result;
    }

    clearBlobCache(): void {
        this.blobCache.clear();
    }

    private computeCacheKey(content: string, options: RenderPdfPrintOptions, entityType?: string): string {
        const input = content + '|' + JSON.stringify(options) + '|' + (entityType ?? '');
        let hash = 2166136261;
        for (let i = 0; i < input.length; i++) {
            hash ^= input.charCodeAt(i);
            hash = Math.imul(hash, 16777619);
        }
        return (hash >>> 0).toString(36);
    }

    buildStyledHtml(content: string, options: TemplatePrintOptions, entityType?: string): string {
        const styles = this.extractStyles();
        const extraCss = this.buildPrintOptionsCss(options);
        const entityCss = entityType ? (this.mapperRegistry.getMapper(entityType)?.getEntityPreviewCss('body') ?? '') : '';
        return `<!DOCTYPE html>
        <html>
        <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <title>Report</title>
        <style>
            ${styles}
            body { margin: 0; padding: ${options.contentPadding ?? DEFAULT_PRINT_OPTIONS.contentPadding}; }
            @page { margin: ${options.margins || DEFAULT_PRINT_OPTIONS.margins}; }
            ${buildPreviewResetCss('body')}
            ${entityCss}
            .header > *, .info-section > *, .employee-section > *, .employee-details > *, .signature-section > * { flex: 1 1 0 !important; min-width: 0 !important; }
            .info-item, .signature-section { flex-wrap: wrap !important; }
            .info-label, .info-value { white-space: normal !important; }
            ${extraCss}
        </style>
        </head>
        <body>${content}</body>
        </html>`;
    }

    private escapeCssString(text: string): string {
        return text.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, ' ');
    }

    private buildPrintOptionsCss(options: TemplatePrintOptions): string {
        const parts: string[] = [];
        const today = new Date().toLocaleDateString('en-GB');

        if (options.showPageNumbers && options.showGenerationDate) {
            parts.push(`@page { @bottom-left { content: "Generated: ${today}"; font-size: 9pt; color: #6b7280; } @bottom-right { content: "Page " counter(page) " of " counter(pages); font-size: 9pt; color: #6b7280; } }`);
        } else if (options.showPageNumbers) {
            parts.push(`@page { @bottom-center { content: "Page " counter(page) " of " counter(pages); font-size: 9pt; color: #6b7280; } }`);
        } else if (options.showGenerationDate) {
            parts.push(`@page { @bottom-center { content: "Generated: ${today}"; font-size: 9pt; color: #6b7280; } }`);
        }

        if (options.headerText?.trim()) {
            parts.push(`@page { @top-center { content: "${this.escapeCssString(options.headerText.trim())}"; font-size: 9pt; color: #6b7280; } }`);
        }

        if (options.footerText?.trim()) {
            const centerOccupied = options.showPageNumbers || options.showGenerationDate;
            const margin = centerOccupied ? '@bottom-left' : '@bottom-center';
            parts.push(`@page { ${margin} { content: "${this.escapeCssString(options.footerText.trim())}"; font-size: 9pt; color: #6b7280; } }`);
        }

        if (options.watermarkText?.trim()) {
            parts.push(`body::before { content: "${this.escapeCssString(options.watermarkText.trim())}"; position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(-45deg); font-size: 6rem; font-weight: 700; color: rgba(0,0,0,0.08); white-space: nowrap; pointer-events: none; z-index: 0; }`);
        }

        return parts.join('\n');
    }

    private async encodeRequestBody(json: string): Promise<{ body: BodyInit; headers: HttpHeaders }> {
        const baseHeaders = new HttpHeaders({ 'Content-Type': 'application/json' });

        if (typeof CompressionStream === 'undefined') {
            return { body: json, headers: baseHeaders };
        }

        const stream = new Blob([json]).stream().pipeThrough(new CompressionStream('gzip'));
        const compressed = await new Response(stream).arrayBuffer();
        return {
            body: compressed,
            headers: baseHeaders.set('Content-Encoding', 'gzip')
        };
    }

    private mapPrintOptions(options: TemplatePrintOptions): RenderPdfPrintOptions {
        return {
            pageSize: (options.pageSize || DEFAULT_PRINT_OPTIONS.pageSize).toUpperCase(),
            pageOrientation: options.pageOrientation || DEFAULT_PRINT_OPTIONS.pageOrientation,
            margins: options.margins || DEFAULT_PRINT_OPTIONS.margins,
            scale: options.scale ?? DEFAULT_PRINT_OPTIONS.scale,
            headerHtml: options.headerHtml ?? DEFAULT_PRINT_OPTIONS.headerHtml,
            footerHtml: options.footerHtml ?? DEFAULT_PRINT_OPTIONS.footerHtml
        };
    }

    private extractStyles(): string {
        let styles = '';

        for (let i = 0; i < document.styleSheets.length; i++) {
            try {
                const rules = document.styleSheets[i].cssRules || document.styleSheets[i].rules;
                if (rules) {
                    for (let j = 0; j < rules.length; j++) {
                        styles += rules[j].cssText + '\n';
                    }
                }
            } catch (e) {
            }
        }

        document.querySelectorAll('style').forEach(tag => {
            styles += tag.innerHTML + '\n';
        });

        // CSSOM cssText turns CSS escapes back into literal characters, so a template rule such as
        // content:"\3c/style\3e..." would otherwise close the raw <style> block of the generated report.
        return escapeStyleClose(styles);
    }

    async injectWidgets(
        container: HTMLElement,
        entityType: string,
        templateContext: TemplateContext,
        trackForCleanup = true,
        previewMode = false
    ): Promise<ComponentRef<unknown>[]> {
        const availableWidgets = getWidgetsForEntityType(entityType);
        const localCreated: ComponentRef<unknown>[] = [];

        availableWidgets.forEach(widget => {
            container.querySelectorAll(widget.tagName).forEach((element: Element) => {
                try {
                    const componentRef = createComponent(widget.component, {
                        environmentInjector: this.environmentInjector,
                        elementInjector: this.injector,
                    });

                    if (previewMode && this.isPreviewAware(componentRef.instance)) {
                        componentRef.instance.previewMode = true;
                    }
                    this.configureWidget(componentRef, element, templateContext, widget.componentName);
                    element.parentNode?.replaceChild(componentRef.location.nativeElement, element);
                    componentRef.changeDetectorRef.detectChanges();

                    if (trackForCleanup) {
                        this.createdComponents.push(componentRef);
                    } else {
                        localCreated.push(componentRef);
                    }
                } catch (error) {
                    if (!environment.production) {
                        console.warn(`Widget injection failed for ${widget.tagName}`, error);
                    }
                }
            });
        });

        return localCreated;
    }

    private isPreviewAware(instance: unknown): instance is { previewMode: boolean } {
        return typeof instance === 'object' && instance !== null && 'previewMode' in instance;
    }

    private configureWidget(componentRef: ComponentRef<unknown>, element: Element, context: TemplateContext, componentName: string): void {
        const widget = getWidgetByComponentName(componentName);
        if (widget) {
            widget.configureInputs(componentRef, context, element as HTMLElement);
        } else if (componentRef.instance && typeof componentRef.instance === 'object' && 'data' in componentRef.instance && context?.computedData) {
            (componentRef.instance as { data: unknown }).data = context.computedData;
        }
    }

    async waitForWidgets(refs: ComponentRef<unknown>[]): Promise<void> {
        if (refs.length === 0) return;

        refs.forEach(ref => ref.changeDetectorRef.detectChanges());

        await new Promise<void>(resolve => {
            const timeoutId = window.setTimeout(() => {
                let settled = false;
                let rafId = 0;
                let capId = 0;

                const settle = (): void => {
                    if (settled) return;
                    settled = true;
                    cancelAnimationFrame(rafId);
                    clearTimeout(capId);
                    this.activeTimeouts = this.activeTimeouts.filter(id => id !== timeoutId && id !== capId);
                    resolve();
                };

                rafId = requestAnimationFrame(() => {
                    rafId = requestAnimationFrame(() => settle());
                });
                capId = window.setTimeout(settle, 300);
                this.activeTimeouts.push(capId);
            }, 100);
            this.activeTimeouts.push(timeoutId);
        });
    }

    cleanupComponents(): void {
        this.createdComponents.forEach(ref => ref.destroy());
        this.createdComponents = [];
        this.activeTimeouts.forEach(id => clearTimeout(id));
        this.activeTimeouts = [];
    }
}
